import {Avatar, Card, Flag, Label, Parameters} from 'platform/components';
import {Box, Heading, HStack, Show, Space, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {join, pipe, reject} from 'ramda';
import {isEmptyArray, isNilOrEmpty, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {GetServiceCheckInApiResponse, GetServiceHandOverApiResponse} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER} from '@dms/shared';

import {parseDate, buildArray, TestIdProps, Nullish} from 'shared/web';

import {useStateCheckinHandoverFlag} from '../../../../../hooks/useStateCheckinHandoverFlag';

interface CheckinHandoverCardProps extends TestIdProps {
  checkin: GetServiceCheckInApiResponse | Nullish;
  handover: GetServiceHandOverApiResponse | Nullish;
}

export function CheckinHandoverCard(props: CheckinHandoverCardProps) {
  const formatDateTime = useDateTimeFormatter();

  const checkinStateFlagProps = useStateCheckinHandoverFlag('CHECKIN', props.checkin?.state);
  const handoverStateFlagProps = useStateCheckinHandoverFlag('HANDOVER', props.handover?.state);

  const checkinDate = props.checkin?.completedDate ?? props.checkin?.scheduledDate;
  const formatedCheckinDate = isNotNil(checkinDate)
    ? formatDateTime('dateTimeShort', parseDate(checkinDate))
    : EMPTY_PLACEHOLDER;
  const checkinParameters = buildArray<string>()
    .when(isNotNilOrEmpty(props.checkin?.driver?.name), props.checkin?.driver?.name as string)
    .when(
      props.checkin?.driver?.phoneNumber,
      joinValuesToString([
        props.checkin?.driver?.phoneNumber?.prefix,
        props.checkin?.driver?.phoneNumber?.number,
      ])
    );

  const handoverDate = props.handover?.completedDate ?? props.handover?.scheduledDate;
  const formatedHandoverDate = isNotNil(handoverDate)
    ? formatDateTime('dateTimeShort', parseDate(handoverDate))
    : EMPTY_PLACEHOLDER;
  const handoverParameters = buildArray<string>()
    .when(isNotNilOrEmpty(props.handover?.driver?.name), props.handover?.driver?.name as string)
    .when(
      props.handover?.driver?.phoneNumber,
      joinValuesToString([
        props.handover?.driver?.phoneNumber?.prefix,
        props.handover?.driver?.phoneNumber?.number,
      ])
    );

  return (
    <Card title={i18n.t('entity.workshop.labels.checkinAndHandover')}>
      <HStack spacing={4}>
        <Box flex={1} backgroundColor="palettes.blue.10.100" padding={4} borderRadius="medium">
          <HStack spacing={4} align="center">
            <Avatar
              variant="square"
              color="palettes.blue.20.100"
              iconColor="palettes.blue.80.100"
              icon="navigation/arrow_upward"
            />
            <VStack>
              <Label>{i18n.t('entity.workshop.labels.checkIn')}</Label>
              <Space vertical={1} />
              <HStack spacing={2} align="center">
                <Heading size={3}>{formatedCheckinDate}</Heading>
                {checkinStateFlagProps && <Flag size="small" {...checkinStateFlagProps} />}
              </HStack>
              <Space vertical={1} />
              <Parameters parameters={checkinParameters} color="secondary" size="small" />
              <Show when={isEmptyArray(checkinParameters)}>{EMPTY_PLACEHOLDER}</Show>
            </VStack>
          </HStack>
        </Box>
        <Box flex={1} backgroundColor="palettes.green.10.100" padding={4} borderRadius="medium">
          <HStack spacing={4} align="center">
            <Avatar
              variant="square"
              color="palettes.green.20.100"
              iconColor="palettes.green.80.100"
              icon="navigation/arrow_downward"
            />
            <VStack>
              <Label>{i18n.t('entity.workshop.labels.handover')}</Label>
              <Space vertical={1} />
              <HStack spacing={2} align="center">
                <Heading size={3}>{formatedHandoverDate}</Heading>
                {handoverStateFlagProps && <Flag size="small" {...handoverStateFlagProps} />}
              </HStack>
              <Space vertical={1} />
              <Parameters parameters={handoverParameters} color="secondary" size="small" />
              <Show when={isEmptyArray(handoverParameters)}>{EMPTY_PLACEHOLDER}</Show>
            </VStack>
          </HStack>
        </Box>
      </HStack>
    </Card>
  );
}

const joinValuesToString = pipe(reject(isNilOrEmpty), join(' '));
